import { useRef } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

import type { NurturingPlanController } from '../hooks/useNurturingPlan';
import { ConfigureInitializeState } from 'app/components/ReusableUIComponents/ConfigureInitializeState';
import { ConfigPanel } from './ConfigPanel';
import { PlanOutputPanel } from './PlanOutputPanel';
import { PlanSkeleton } from './PlanSkeleton';

interface NurturingPlanGeneratorProps {
  ctrl: NurturingPlanController;
}

export const NurturingPlanGenerator = ({ ctrl }: NurturingPlanGeneratorProps) => {
  const { plan, config, activeTab, setActiveTab } = ctrl;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // While the version list is loading we don't yet know whether a config exists,
  // so avoid flashing the default/fallback plan.
  if (ctrl.versionsLoading) {
    return (
      <ConfigureInitializeState
        loading
        loadingMessage="Loading nurturing plan versions..."
        message=""
      />
    );
  }

  // No plan config exists for this dealer yet: show only the Initialize action,
  // not the default/fallback plan data (mirrors Nurturing Journey Settings).
  if (ctrl.showInitialize) {
    const cdpError =
      ctrl.segmentsConfigError ||
      (ctrl.cdpDataMissing && ctrl.cdpMissingReason ? ctrl.cdpMissingReason : null);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
          px: 2,
          py: 2,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 640 }}>
          <ConfigureInitializeState
            loading={ctrl.checkingInitializeEligibility}
            loadingMessage={
              ctrl.segmentsConfigLoading
                ? 'Loading dealer segment settings...'
                : 'Checking CDP data eligibility for plan initialization...'
            }
            errorMessage={cdpError}
            message={
              ctrl.cdpDataMissing
                ? 'No nurturing plan generator config found. Upload CDP data or initialize with default settings.'
                : 'No nurturing plan generator config found. Initialize from CDP lead analysis when data is available.'
            }
            onInitialize={!ctrl.cdpDataMissing ? ctrl.handleInitialize : undefined}
            initializing={ctrl.initializing}
            buttonLabel="Initialize Nurturing Plan Generator"
            actions={
              ctrl.cdpDataMissing && !ctrl.checkingInitializeEligibility ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ display: 'flex', justifyContent: 'center', my: 3 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                      ctrl.initializingDefault ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SettingsBackupRestoreIcon />
                      )
                    }
                    onClick={ctrl.handleInitializeDefault}
                    disabled={ctrl.initializingDefault || !!ctrl.segmentsConfigError}
                  >
                    {ctrl.initializingDefault
                      ? 'Initializing...'
                      : 'Initialize with default settings'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UploadFileIcon />}
                    onClick={ctrl.handleUploadCdpData}
                  >
                    Upload CDP data
                  </Button>
                </Stack>
              ) : undefined
            }
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-guidance-tour="configure-nurturing-plan-generator-page"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <Box sx={{ display: 'flex', gap: 2.5, flex: 1, minHeight: 0 }}>
        <ConfigPanel
          config={config}
          rawConfig={ctrl.rawConfig}
          segments={ctrl.segments}
          pctSum={ctrl.pctSum}
          onConfigChange={ctrl.onConfigChange}
          onWarmupScheduleChange={ctrl.onWarmupScheduleChange}
          onEagerChange={ctrl.onEagerChange}
          onLookBackRangeChange={ctrl.onLookBackRangeChange}
          onSegmentNameChange={ctrl.onSegmentNameChange}
          onSegmentStartDayChange={ctrl.onSegmentStartDayChange}
          onPctChange={ctrl.onPctChange}
          onAddSegment={ctrl.onAddSegment}
          onDeleteSegment={ctrl.onDeleteSegment}
          onReorderSegments={ctrl.onReorderSegments}
          onGeneratePlan={ctrl.handleGeneratePlan}
          generating={ctrl.loading}
          canGenerate={ctrl.canGenerate}
          availableSegments={ctrl.availableSegments}
          segmentsConfigError={ctrl.segmentsConfigError}
          canAddSegment={ctrl.canAddSegment}
          hasLaunchDate={ctrl.hasLaunchDate}
          launchDateDisplay={ctrl.launchDateDisplay}
          leadAnalysisLoading={ctrl.leadAnalysisLoading}
        />

        <Box
          ref={scrollContainerRef}
          sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', pr: 1 }}
        >
          {!plan || ctrl.loading || ctrl.saving || ctrl.settingsLoading ? (
            <Box>
              {(ctrl.loading || ctrl.saving || ctrl.settingsLoading) && (
                <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    {ctrl.settingsLoading
                      ? 'Loading plan settings...'
                      : ctrl.saving
                        ? 'Saving plan...'
                        : 'Generating plan...'}
                  </Typography>
                </Stack>
              )}
              <PlanSkeleton />
            </Box>
          ) : (
            <PlanOutputPanel
              plan={plan}
              config={config}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              openBatches={ctrl.openBatches}
              toggleBatch={ctrl.toggleBatch}
              scrollContainerRef={scrollContainerRef}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NurturingPlanGenerator;
